import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Pencil, Shield, Loader2 } from 'lucide-react';
import { getPolicies, deletePolicy, savePolicy } from '../services/api';
import { PageHeader } from '../components/ui/page-header';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tooltip } from '../components/ui/tooltip';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '../components/ui/modal';
import { EmptyState } from '../components/ui/empty-state';

function PoliciesPage() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);

  useEffect(() => {
    loadPolicies();
  }, []);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      const response = await getPolicies();
      setPolicies(response.data.data || []);
    } catch (err) {
      console.error('Failed to load policies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (policyName) => {
    if (window.confirm(`Delete policy "${policyName}"?`)) {
      try {
        await deletePolicy(policyName);
        loadPolicies();
      } catch (err) {
        console.error('Failed to delete policy:', err);
      }
    }
  };

  const handleEdit = (policy) => {
    setEditingPolicy(policy);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      await savePolicy(editingPolicy);
      setDialogOpen(false);
      setEditingPolicy(null);
      loadPolicies();
    } catch (err) {
      console.error('Failed to save policy:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
      <PageHeader
        title="DLP Policies"
        description="Define and manage data loss prevention rules."
        actions={
          <Button
            onClick={() => {
              setEditingPolicy({
                name: 'New Policy',
                description: '',
                enabled: true,
                rules: [],
              });
              setDialogOpen(true);
            }}
          >
            <Plus />
            New Policy
          </Button>
        }
      />

      {policies.length === 0 ? (
        <EmptyState
          icon={Shield}
          title="No policies configured"
          description="Create a policy to get started with data loss prevention."
        />
      ) : (
        <div className="flex flex-col gap-3">
          {policies.map((policy) => (
            <Card key={policy.name}>
              <CardContent className="flex items-center gap-4 p-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Shield className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-foreground">{policy.name}</div>
                  <div className="truncate text-sm text-muted-foreground">
                    {policy.description || 'No description'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={policy.enabled ? 'success' : 'outline'}>
                    {policy.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                  <Badge variant="secondary">{policy.rule_count || 0} rules</Badge>
                  <Tooltip label="Edit">
                    <Button variant="ghost" size="icon-sm" onClick={() => handleEdit(policy)}>
                      <Pencil />
                    </Button>
                  </Tooltip>
                  <Tooltip label="Delete">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(policy.name)}
                    >
                      <Trash2 />
                    </Button>
                  </Tooltip>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Modal open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <ModalHeader>
          <ModalTitle>Edit Policy</ModalTitle>
        </ModalHeader>
        <ModalBody className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Policy Name</label>
            <Input
              value={editingPolicy?.name || ''}
              onChange={(e) =>
                setEditingPolicy({ ...editingPolicy, name: e.target.value })
              }
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Description</label>
            <Textarea
              rows={3}
              value={editingPolicy?.description || ''}
              onChange={(e) =>
                setEditingPolicy({ ...editingPolicy, description: e.target.value })
              }
            />
          </div>
          <Alert variant="info">
            <AlertDescription>
              Policy rules can be edited in JSON format. Use the API or configuration files for
              advanced editing.
            </AlertDescription>
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default PoliciesPage;
